import { useEffect, useState } from "react"
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import GenericDialog from "@cmp/genericDialog"
import { ProgramClient } from "@clients/programClient"
import { TermDto } from "@clients/termClient"
import { TrackDto } from "@masterschool/course-builder-api"
import { fetchPrograms } from "@features/program/programSliceThunks"
import { selectTerms } from "@features/term/termSelector"
import { fetchTerms } from "@features/term/termSlice"
import { selectAllTracks } from "@features/tracks/trackSelectors"
import { fetchTracks } from "@features/tracks/trackSlice"
import { showErrorSnackbar, showSuccessSnackbar } from "@features/ui/uiSlice"

// A term that started up to a month ago is still selectable: the hourly
// registration job looks back one month, so a program created for such a term
// still gets its students registered to the term's course classes.
const TERM_LOOKBACK_DAYS = 30

// Creates the program the create-track-programs job would have created for
// this (track, term) pair: the campus program anchored to the term's cohort,
// the track linked as its syllabus, and the default program class. The server
// call is idempotent — if the program already exists nothing is written.
function CreateTrackProgramPopup(props: {
  open: boolean
  onClose: () => void
}) {
  const { open, onClose } = props
  const dispatch = useAppDispatch()
  const tracks = useAppSelector(selectAllTracks)
  const terms = useAppSelector(selectTerms)
  const [trackId, setTrackId] = useState("")
  const [termId, setTermId] = useState("")
  const [isPending, setIsPending] = useState(false)

  const tracksCount = tracks.length
  const termsCount = terms.length
  useEffect(() => {
    if (!open) {
      return
    }
    if (tracksCount === 0) {
      dispatch(fetchTracks())
    }
    if (termsCount === 0) {
      dispatch(fetchTerms())
    }
  }, [open, tracksCount, termsCount, dispatch])

  const selectableTracks = tracks
    .filter((track) => !track.isArchived)
    .sort((a, b) => trackLabel(a).localeCompare(trackLabel(b)))

  const earliestSelectableStart = new Date()
  earliestSelectableStart.setDate(
    earliestSelectableStart.getDate() - TERM_LOOKBACK_DAYS,
  )
  const selectableTerms = terms
    .filter((term) => new Date(term.startDate) >= earliestSelectableStart)
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )

  const closeAndReset = () => {
    setTrackId("")
    setTermId("")
    onClose()
  }

  const onSubmit = () => {
    setIsPending(true)
    ProgramClient.createTrackProgram({ termId, trackId })
      .then((isNewlyCreated) => {
        dispatch(
          showSuccessSnackbar(
            isNewlyCreated
              ? "Program created"
              : "A program for this track and term already exists",
          ),
        )
        dispatch(fetchPrograms())
        closeAndReset()
      })
      .catch((error) => {
        dispatch(
          showErrorSnackbar(
            error instanceof Error ? error.message : "Failed to create program",
          ),
        )
      })
      .finally(() => setIsPending(false))
  }

  return (
    <GenericDialog
      open={open}
      size="xs"
      onClose={closeAndReset}
      title="Add program from track"
      subtitle="Creates the program for the selected term, with the track as its syllabus and its default class."
      content={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            mt: "16px",
          }}
        >
          <FormControl fullWidth required>
            <InputLabel sx={{ fontSize: "17px" }}>Track</InputLabel>
            <Select
              label="Track"
              value={trackId}
              onChange={(e) => setTrackId(e.target.value)}
            >
              {selectableTracks.map((track) => (
                <MenuItem key={track.id} value={track.id}>
                  {trackLabel(track)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel sx={{ fontSize: "17px" }}>Term</InputLabel>
            <Select
              label="Term"
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
            >
              {selectableTerms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {termLabel(term)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
      buttons={[
        {
          type: "secondary",
          text: "Cancel",
          onClick: closeAndReset,
        },
        {
          type: "primary",
          text: "Add program",
          onClick: onSubmit,
          disabled: !trackId || !termId || isPending,
          loading: isPending,
        },
      ]}
    />
  )
}

function trackLabel(track: TrackDto): string {
  return track.internalName || track.externalName || track.id
}

function termLabel(term: TermDto): string {
  return new Date(term.startDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

export default CreateTrackProgramPopup
